import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import supabase from "../config/supabaseConfig";
import logoTelao from "../assets/logo-telao-branca.png";
import "./Telao.css";

// Telão dinâmico (patch 2026) — layout de docs/prototipos/telao.html.
// Dados reais: aulas em_andamento + aula_alunos (select direto, sem o
// embed alunos(*) do aulasService — colide com a coluna jsonb de aulas).
const REFRESH_MS = 45000;
const CARDS_POR_PAGINA = 10;
const PAGINACAO_MS = 10000;

const Telao = () => {
  const [salas, setSalas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [agora, setAgora] = useState(new Date());
  const [pagina, setPagina] = useState(0);

  useEffect(() => {
    let ativo = true;

    const carregar = async () => {
      try {
        const { data: aulas, error } = await supabase
          .from("aulas")
          .select("id, professor:professor_id(id, nome)")
          .eq("status", "em_andamento")
          .order("created_at", { ascending: true });
        if (error) throw error;

        const aulaIds = (aulas || []).map((a) => a.id);
        let porAula = new Map();

        if (aulaIds.length > 0) {
          // Quem FALTOU sai do telão (decisão Rodrigo 27/07): a tela mostra
          // quem está treinando agora, e o aluno marcado como falta não está.
          // Antes ele seguia exibido até a aula ser finalizada.
          const { data: aa, error: aaError } = await supabase
            .from("aula_alunos")
            .select("aula_id, aluno_id")
            .in("aula_id", aulaIds)
            .neq("status", "falta");
          if (aaError) throw aaError;

          const alunoIds = [...new Set((aa || []).map((r) => r.aluno_id))];
          let alunosMap = new Map();
          if (alunoIds.length > 0) {
            const { data: alunos, error: alunosError } = await supabase
              .from("alunos")
              .select("id, nome")
              .in("id", alunoIds);
            if (alunosError) throw alunosError;
            alunosMap = new Map((alunos || []).map((a) => [a.id, a]));
          }

          (aa || []).forEach((r) => {
            const aluno = alunosMap.get(r.aluno_id);
            if (!aluno) return;
            if (!porAula.has(r.aula_id)) porAula.set(r.aula_id, []);
            porAula.get(r.aula_id).push(aluno);
          });
        }

        const salasMontadas = (aulas || [])
          .filter((a) => a.professor)
          .map((a) => ({
            id: a.id,
            professor: a.professor.nome,
            alunos: porAula.get(a.id) || [],
          }));

        if (ativo) setSalas(salasMontadas);
      } catch (e) {
        console.error("Erro ao carregar telão:", e);
      } finally {
        if (ativo) setCarregando(false);
      }
    };

    carregar();
    const interval = setInterval(carregar, REFRESH_MS);
    return () => {
      ativo = false;
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const relogio = setInterval(() => setAgora(new Date()), 30000);
    return () => clearInterval(relogio);
  }, []);

  const totalPaginas = Math.max(1, Math.ceil(salas.length / CARDS_POR_PAGINA));

  // Rotação automática entre páginas quando há mais de 10 professores.
  useEffect(() => {
    if (totalPaginas <= 1) {
      setPagina(0);
      return;
    }
    const rotacao = setInterval(() => {
      setPagina((p) => (p + 1) % totalPaginas);
    }, PAGINACAO_MS);
    return () => clearInterval(rotacao);
  }, [totalPaginas]);

  const hora = agora.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const totalAlunos = salas.reduce((soma, s) => soma + s.alunos.length, 0);
  const paginaAtual = pagina % totalPaginas;
  const salasPagina = salas.slice(
    paginaAtual * CARDS_POR_PAGINA,
    paginaAtual * CARDS_POR_PAGINA + CARDS_POR_PAGINA
  );

  return (
    <div className="telao-real">
      <div className="tr-head">
        <img
          className="tr-brand-img"
          src={logoTelao}
          alt="Iron House — Health & Performance"
        />
        <div className="tr-clock">
          {hora}
          <small>
            {salas.length > 0
              ? `Aula em andamento · ${salas.length} professor${
                  salas.length > 1 ? "es" : ""
                }`
              : "Nenhuma aula em andamento"}
          </small>
        </div>
      </div>

      {!carregando && salas.length === 0 && (
        <div className="tr-vazio">
          <img
            className="tr-brand-img tr-brand-img-lg"
            src={logoTelao}
            alt="Iron House — Health & Performance"
          />
          <p>Aguardando início das aulas...</p>
        </div>
      )}

      {salas.length > 0 && (
        <div className="tr-salao">
          {salasPagina.map((s) => (
            <div className="tr-col" key={s.id}>
              <h3 title={s.professor}>
                {/* Só o primeiro nome: o completo truncava com "…" no card */}
                <span className="tr-col-nome">
                  {(s.professor || "").split(" ")[0]}
                </span>
                <span className="tr-col-count">{s.alunos.length}</span>
              </h3>
              <ul>
                {s.alunos.map((a) => (
                  <li key={a.id} title={a.nome}>
                    {a.nome}
                  </li>
                ))}
                {s.alunos.length === 0 && <li className="tr-sem">—</li>}
              </ul>
            </div>
          ))}
        </div>
      )}

      {salas.length > 0 && (
        <div className="tr-rodape">
          <span className="tr-rodape-total">
            {totalAlunos} aluno(s) em aula agora
          </span>
          {totalPaginas > 1 && (
            <span className="tr-rodape-pag">
              Página {paginaAtual + 1} de {totalPaginas}
            </span>
          )}
        </div>
      )}

      {/* "/" = HomeRedirect decide por papel: recepção volta pra Pré-sala,
          demais pra Sala (pedido Rodrigo 15/07 — era /sala fixo). */}
      <Link to="/" className="tr-voltar">
        ← Voltar
      </Link>
    </div>
  );
};

export default Telao;
